import type {
  ShipmentCurrentExtras,
  PieceCurrentExtras,
  ServiceDisplay,
} from "./api/shipment-current";
// ============================================================================
// Core Types - Based on actual Prisma schema from ezx-server
// ============================================================================

// Type-only, so it is erased at build time and introduces no runtime import.
// `Carrier` has one home (see the Carrier Models section below for why).
import type { Carrier } from "@/api/carriers";

// Generic Types
export interface PaginatedResponse<T> {
  data: T[];
  metadata?: {
    total: number;
    page: number;
    limit: number;
    totalPages?: number;
  };
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages?: number;
  };
}

// Enums
export enum CustomerType {
  INDIVIDUAL = "INDIVIDUAL",
  BUSINESS = "BUSINESS",
}

// No CarrierType: there is no such concept server-side. How EZX procures from
// a carrier is a SupplyChannel, and a carrier can have several at once.

// OrderDepotTransitStatus removed - depot transit is now tracked at Shipment level

export enum TaskType {
  // Customer operations
  CUSTOMER_PICKUP = "CUSTOMER_PICKUP",
  CUSTOMER_DELIVERY = "CUSTOMER_DELIVERY",
  CUSTOMER_RETURN = "CUSTOMER_RETURN",

  // Carrier operations
  CARRIER_DROPOFF = "CARRIER_DROPOFF",
  CARRIER_PICKUP = "CARRIER_PICKUP",

  // Internal depot operations
  DEPOT_RECEIVE = "DEPOT_RECEIVE",
  DEPOT_SORT = "DEPOT_SORT",
  DEPOT_DISPATCH = "DEPOT_DISPATCH",
  DEPOT_TRANSFER = "DEPOT_TRANSFER",
}

export enum TaskStatus {
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
}

export enum TripStatus {
  PLANNED = "PLANNED",
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  ARCHIVED = "ARCHIVED",
}

export enum Provider {
  LOCAL = "LOCAL",
  SHIPSTATION = "SHIPSTATION",
  CHITCHATS = "CHITCHATS",
  SHIPENGINE = "SHIPENGINE",
  EASYPOST = "EASYPOST",
  DIRECT_CP = "DIRECT_CP",
  DIRECT_UPS = "DIRECT_UPS",
  DIRECT_FEDEX = "DIRECT_FEDEX",
  DIRECT_USPS = "DIRECT_USPS",
  CUSTOM = "CUSTOM",
}

export enum ShipmentStatus {
  PENDING_PAYMENT = "PENDING_PAYMENT",
  LABEL_READY = "LABEL_READY",
  PICKED_UP = "PICKED_UP",
  IN_TRANSIT = "IN_TRANSIT",
  AT_FACILITY = "AT_FACILITY",
  OUT_FOR_DELIVERY = "OUT_FOR_DELIVERY",
  DELIVERED = "DELIVERED",
  AVAILABLE_FOR_PICKUP = "AVAILABLE_FOR_PICKUP",
  DELIVERY_ATTEMPTED = "DELIVERY_ATTEMPTED",
  EXCEPTION = "EXCEPTION",
  ON_HOLD = "ON_HOLD",
  RETURNED = "RETURNED",
  CANCELLED = "CANCELLED",
}

export enum ResolutionMode {
  SHOW_BOTH = "SHOW_BOTH",
  PREFER_PROVIDER = "PREFER_PROVIDER",
  PREFER_LOWEST = "PREFER_LOWEST",
}

export enum PropertyType {
  HOUSE = "HOUSE",
  CONDO_LOBBY = "CONDO_LOBBY",
  CONDO_UNIT = "CONDO_UNIT",
  OFFICE = "OFFICE",
  WAREHOUSE = "WAREHOUSE",
  RETAIL = "RETAIL",
}

export enum DepotType {
  HUB = "HUB",
  WAREHOUSE = "WAREHOUSE",
  MICRO_HUB = "MICRO_HUB",
}

// ============================================================================
// Core Models
// ============================================================================

export interface Address {
  plusCode?: string | null;
  id: string;
  line1: string;
  line2?: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  location?: {
    lat: number;
    lng: number;
  };
  isValidated?: boolean;
  validatedAt?: string;
  validationProvider?: string;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  name?: string; // Computed from firstName/lastName
  phone?: string;
  picture?: string;
  role?: string;
  roles?: string[];
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface Customer {
  id: string;
  customerNumber: number;
  type: CustomerType;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  businessName?: string;
  businessNumber?: string;
  businessAccount?: BusinessAccount;
  status: string;
  /** Active carrier connections (status ACTIVE, not customer-removed). */
  carrierConnections?: CustomerCarrierConnectionSummary[];
  billing?: CustomerBilling;
  billingId?: string;
  defaultAddressId?: string;
  defaultAddress?: Address;
  metadata?: Record<string, unknown>;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  emailVerifiedAt?: string | null;
  phoneVerifiedAt?: string | null;
  isTestAccount?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessAccount {
  id: string;
  customerId: string;
  companyName: string;
  taxNumber?: string;
  registrationNo?: string;
  creditLimit?: number;
  paymentTerms?: string;
  settings?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerCarrierConnectionSummary {
  carrier: { name: string };
  tier?: { name: string } | null;
}

export interface CustomerBilling {
  id: string;
  customerId?: string;
  stripeCustomerId?: string;
  defaultPaymentMethodId?: string;
  billingEmail?: string;
  billingAddressId?: string;
  billingAddress?: Address;
  addressId?: string;
  address?: Address;
  autoCharge?: boolean;
  creditLimit?: number;
  creditUsed?: number;
  lastPaymentDate?: string;
  nextBillingDate?: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Carrier Models
// ============================================================================

// `Carrier` intentionally lives in ONE place: @/api/carriers. There used to be
// a second hand-written copy here, and the two drifted — this one still modelled
// a single `apiProviderId` on the carrier, the shape from before procurement
// became a SupplyChannel. Every dead field then had to be deleted twice, and the
// ones that existed in only one copy survived. Import the type from there.

export interface Depot {
  id: string;
  name: string;
  depotNumber: string;
  type: "HUB" | "WAREHOUSE" | "MICRO_HUB";
  status: "ACTIVE" | "INACTIVE" | "DELETED";
  capacity?: {
    small: number;
    medium: number;
    large: number;
  };
  currentCapacity?: {
    small: number;
    medium: number;
    large: number;
  };
  addressId: string;
  address?: Address;
  serviceRadius?: number;
  operatingHours?: {
    day: number;
    open: string;
    close: string;
  }[];
  // Contact info (optional - may not be in all responses)
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  // Carrier depot fields (merged)
  carrierId?: string;
  carrier?: Carrier;
  code?: string;
  active?: boolean;
  isDefault?: boolean;
  serviceCodes?: string[];
  postalCodes?: string[];
  cutoffTime?: string;
  serviceAreas?: ServiceArea[];
  // Capability flags
  canHandlePickup?: boolean;
  canHandleDelivery?: boolean;
  canHandleReturn?: boolean;
  canHandleTransfer?: boolean;
  _count?: {
    tasks?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface Operator {
  id: string;
  status: "ON_DUTY" | "OFF_DUTY" | "BLOCKED";
  carrierUserId: string;
  carrierUser?: {
    id: string;
    email: string | null;
    name: string | null;
    phone: string | null;
  };
  dispatchCenterId?: string | null;
  dispatchCenter?: {
    id: string;
    name: string;
    code?: string;
  } | null;
  depotId: string;
  depot?: Depot;
  vehicleId?: string;
  vehicle?: Vehicle;
  lastLocation?: {
    lat: number;
    lng: number;
    accuracy?: number;
    altitude?: number;
    heading?: number;
    speed?: number;
    timestamp: string;
  };
  lastLocationTime?: string;
  isTracking?: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Order & Shipment Models
// ============================================================================

export enum OrderStatus {
  REGISTERED = "REGISTERED",
  PENDING = "PENDING",
  CONFIRMED = "CONFIRMED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED",
  // Legacy statuses that may still be in use
  PICKUP_PENDING = "PICKUP_PENDING",
  OUT_FOR_PICKUP = "OUT_FOR_PICKUP",
  PICKUP_ASSIGNED = "PICKUP_ASSIGNED",
  PICKUP_COMPLETED = "PICKUP_COMPLETED",
  PICKUP_FAILED = "PICKUP_FAILED",
  PICKUP_CANCELLED = "PICKUP_CANCELLED",
  PICKED_UP = "PICKED_UP",
  DEPOT_RECEIVE_PENDING = "DEPOT_RECEIVE_PENDING",
  DEPOT_RECEIVE_IN_PROGRESS = "DEPOT_RECEIVE_IN_PROGRESS",
  DEPOT_RECEIVE_COMPLETED = "DEPOT_RECEIVE_COMPLETED",
  DEPOT_RECEIVE_FAILED = "DEPOT_RECEIVE_FAILED",
  DEPOT_RECEIVE_CANCELLED = "DEPOT_RECEIVE_CANCELLED",
  AT_DEPOT = "AT_DEPOT",
  DEPOT_SORT_PENDING = "DEPOT_SORT_PENDING",
  DEPOT_SORT_IN_PROGRESS = "DEPOT_SORT_IN_PROGRESS",
  DEPOT_SORT_COMPLETED = "DEPOT_SORT_COMPLETED",
  DEPOT_SORT_FAILED = "DEPOT_SORT_FAILED",
  DEPOT_SORT_CANCELLED = "DEPOT_SORT_CANCELLED",
  SORTED = "SORTED",
  DEPOT_DISPATCH_PENDING = "DEPOT_DISPATCH_PENDING",
  DEPOT_DISPATCH_IN_PROGRESS = "DEPOT_DISPATCH_IN_PROGRESS",
  DEPOT_DISPATCH_COMPLETED = "DEPOT_DISPATCH_COMPLETED",
  DEPOT_DISPATCH_FAILED = "DEPOT_DISPATCH_FAILED",
  DEPOT_DISPATCH_CANCELLED = "DEPOT_DISPATCH_CANCELLED",
  DISPATCHED = "DISPATCHED",
  IN_TRANSIT = "IN_TRANSIT",
  DELIVERY_PENDING = "DELIVERY_PENDING",
  OUT_FOR_DELIVERY = "OUT_FOR_DELIVERY",
  DELIVERY_ASSIGNED = "DELIVERY_ASSIGNED",
  DELIVERY_COMPLETED = "DELIVERY_COMPLETED",
  DELIVERED = "DELIVERED",
  DELIVERY_ATTEMPTED = "DELIVERY_ATTEMPTED",
  DELIVERY_FAILED = "DELIVERY_FAILED",
  DELIVERY_CANCELLED = "DELIVERY_CANCELLED",
  RETURN_PENDING = "RETURN_PENDING",
  RETURN_IN_PROGRESS = "RETURN_IN_PROGRESS",
  RETURN_COMPLETED = "RETURN_COMPLETED",
  RETURN_FAILED = "RETURN_FAILED",
  RETURN_CANCELLED = "RETURN_CANCELLED",
  RETURNED = "RETURNED",
}

export interface Order {
  id: string;
  orderNumber: number;
  placerCustomerId?: string;
  placerCustomer?: Customer;

  // Timing
  createdAt: string;
  updatedAt: string;

  // Payment fields
  paymentStatus?: "PENDING" | "PAID" | "PARTIAL" | "REFUNDED" | "FAILED";
  paymentMethodType?:
    | "SAVED_CARD"
    | "CREDIT"
    | "INVOICE"
    | "CASH"
    | "E_TRANSFER"
    | "WECHAT";
  totalCents?: number;
  paidAmountCents?: number;
  balanceCents?: number;

  metadata?: Record<string, unknown>;

  // Relations
  shipments?: Shipment[];
}

// OrderDepotTransit removed - depot transit is now tracked at Shipment level
// OrderTracking removed - tracking is now at Shipment level

export interface Shipment extends ShipmentCurrentExtras {
  id: string;
  shipmentNumber: number;
  orderId: string;
  order?: Order;

  // Carrier and service
  carrierId: string;
  carrier?: Carrier;
  /** Pricing-redesign: FK to normalized CarrierService (replaces metadata blob). */
  carrierServiceId?: string;
  carrierService?: ServiceDisplay | null;
  providerCredentialId?: string;
  providerRef?: string;
  status: ShipmentStatus;
  /** Set while an operator/customer cancellation request awaits admin review. */
  cancellationRequestedAt?: string | null;

  // Pickup add-on (pricing-redesign)
  /** FK to PickupBooking; many shipments can share one booking. */
  pickupBookingId?: string;
  pickupBooking?: PickupBooking;
  /** Frozen at order time when pickup is attached; null when no pickup. */
  pickupPriceCents?: number;

  // Pricing
  sellAmountCents?: number;
  /**
   * ⚠️ `buyAmountCents` is DELETED, not hidden.
   *
   * It was a copy of `sellAmountCents` until the ledger made it real — at which
   * point it became a live customer-facing margin leak in handlers that had never
   * changed. What a carrier is OWED is now a PAYABLE_ACCRUAL FinancialEvent on the
   * leg, reconcilable against money that actually moved.
   *
   * Do not re-add it. A number here that nobody reconciles is how a carrier gets
   * paid the wrong amount.
   */

  // Carrier details
  carrierCode?: string;
  serviceCode?: string;
  packageCode?: string;

  // Tracking
  trackingNumber?: string;
  trackingUrl?: string;
  masterTracking?: string;

  // Timing
  shipDate?: string;
  estimatedDelivery?: string;
  actualDelivery?: string;

  // Data
  options?: Record<string, unknown>;
  dimensions?: Record<string, unknown>;
  totalWeight?: number;
  metadata?: Record<string, unknown>;
  providerMetadata?: Record<string, unknown>;
  /** Pricing-redesign: per-shipment delivery notes (printed on labels). */
  deliveryNotes?: string | null;

  // Addresses (via ShipmentAddress relation) — Pattern B (#258)
  addresses?: ShipmentAddress[];

  // Relations
  packages?: ShipmentPackage[];
  tasks?: Task[];
  quotes?: ShipmentQuote[];

  createdAt: string;
  updatedAt: string;
}

export interface ShipmentAddress {
  id: string;
  shipmentId: string;
  addressId?: string;
  address?: Address;
  type: "FROM" | "TO" | "RETURN";

  // Contact info
  contactName?: string;
  contactCompany?: string;
  contactPhone?: string;
  contactEmail?: string;

  // Denormalized address fields
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressProvince?: string;
  addressCountry?: string;
  addressPostalCode?: string;
  addressPlusCode?: string;
  addressLocation?: { lat: number; lng: number };

  // Validation
  isValidated: boolean;
  validatedAt?: string;
  validationProvider?: string;
  validationConfidence?: string;

  propertyResidential?: boolean;
  // Metadata
  isResidential: boolean;
  instructions?: string;
  providerData?: Record<string, unknown>;

  createdAt: string;
  updatedAt: string;
}

export interface ShipmentPackage extends PieceCurrentExtras {
  id: string;
  shipmentId: string;
  index: number;
  weightGrams: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  declaredValueCents?: number;
  packageTypeId?: string;
  providerRef?: string;

  // Package-level tracking
  individualTrackingNumber?: string;
  labelUrl?: string;

  // Special handling
  isFragile: boolean;
  requiresCooling: boolean;
  description?: string;

  // QR code
  qrcode?: string;
  qrcodeData?: string;
}

export interface ShipmentQuote {
  id: string;
  shipmentId: string;
  provider: Provider;
  carrierCode?: string;
  serviceCode?: string;
  serviceName?: string;

  // Rate information
  listRate: number;
  retailRate: number;
  currency: string;

  // Delivery estimates
  estimatedDays?: number;
  estimatedDelivery?: string;

  // Metadata
  providerRef?: string;
  metadata?: Record<string, unknown>;
  expiresAt?: string;

  createdAt: string;
}

// ============================================================================
// Task & Trip Models
// ============================================================================

export interface Task {
  canReceiveCustomerDropoff?: boolean;
  id: string;
  taskNumber: number;
  type: TaskType;
  status: TaskStatus;
  /** Server-derived operational presentation while cancellation is reviewed. */
  displayStatus?: TaskStatus | "ON_HOLD";
  cancellationReviewPending?: boolean;
  workHold?: {
    active: true;
    code: "CANCELLATION_REVIEW_PENDING";
    reason: "CANCELLATION_REVIEW";
    requestedAt: string;
  } | null;
  description?: string;

  // Contact information
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;

  // Timing
  scheduledDate?: string | null;
  schedulingTimeZone?: string | null;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;

  // Dispatch-center receiving (lean "A"): readyAt = run-eligible time,
  // receivedAt = in-hand discriminator (!= null ⇒ physically received at the
  // dispatch center, eligible for the manual backlog). null = awaiting receipt.
  readyAt?: string | null;
  receivedAt?: string | null;

  // Route-manager F2: pickup time window lifted from
  // shipment.pickupBooking.slot. null for non-pickup tasks or pickups
  // without a booking. The dashboard combines this + browser TZ +
  // current time to compute urgency state (overdue / starting-soon /
  // in-progress / upcoming).
  scheduledWindow?: {
    slotId: string;
    date: string; // YYYY-MM-DD (PickupBooking.scheduledDate, calendar date)
    startTime: string; // HH:MM
    endTime: string; // HH:MM
    timeZone: string;
    startAt: string;
    endAt: string;
  } | null;

  // Completion data
  proof?: Proof;
  failureReason?: string;
  failureReasonCode?: number;

  // Relations - NEW structure
  shipmentId?: string;
  shipment?: Shipment;
  shipmentSnapshot?: {
    shipmentNumber?: number;
    [key: string]: unknown;
  };

  waypointId?: string;
  waypoint?: TripWaypoint;

  tripId?: string;
  trip?: Trip;
  dispatchCenterId?: string | null;

  addressId?: string;
  address?: Address;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressLatitude?: number | string | null;
  addressLongitude?: number | string | null;
  addressInstructions?: string | null;

  operatorId?: string;
  operator?: Operator;

  depotId?: string;
  depot?: Depot;

  // Parent/child relationships
  parentTaskId?: string;
  parentTask?: Task;
  childTasks?: Task[];

  // DEPRECATED - for backward compatibility
  orderId?: string;
  order?: Order;
}

export interface Trip {
  id: string;
  tripNumber: number;
  status: TripStatus;
  color?: string;

  // Timing
  startTime?: string;
  endTime?: string;
  estimatedDuration?: number; // minutes
  actualDuration?: number | null; // minutes

  // Route
  startLocation?: { lat: number; lng: number; address?: string };
  endLocation?: { lat: number; lng: number; address?: string };
  useCustomStartLocation: boolean;
  useCustomEndLocation: boolean;
  plannedRoute?: {
    routes?: {
      polyline?: {
        encodedPolyline?: string;
      };
    }[];
  };
  actualRoute?: unknown; // Actual GPS path
  distance?: number; // km

  // Progress
  pendingTasks?: number;
  inProgressTasks?: number;
  cancelledTasks?: number;
  completedTasks: number;
  failedTasks: number;
  totalTasks: number;

  // Metadata
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;

  // Relations
  operatorId?: string;
  operator?: Operator;

  vehicleId?: string;
  vehicle?: Vehicle | null;

  depotId: string;
  depot?: Depot;
  dispatchCenterId?: string;
  dispatchCenter?: Pick<DispatchCenter, "id" | "name" | "code">;

  waypoints?: TripWaypoint[];
  tasks?: Task[];
}

export interface TripWaypoint {
  id: string;
  tripId: string;
  trip?: Trip;
  sequence: number;
  locationKey: string;
  radius?: number;

  // Relations
  tasks?: Task[];
  eta?: TripWaypointETA;

  createdAt: string;
  updatedAt: string;
}

export interface TripWaypointETA {
  id: string;
  waypointId: string;
  estimatedArrivalTime: string;
  estimatedDepartureTime: string;
  estimatedDuration: number; // minutes
  actualArrivalTime?: string;
  actualDepartureTime?: string;
  lastUpdated: string;
}

export interface Vehicle {
  id: string;
  vehicleNumber: string;
  /** Server's unique vehicle identifier (Vehicle.code); list endpoints return
   * this rather than vehicleNumber. */
  code?: string;
  type: string;
  make?: string;
  model?: string;
  year?: number;
  licensePlate: string;
  vin?: string;
  status: string;
  capacity?: Record<string, unknown>;
  currentLoad?: Record<string, unknown>;
  lastMaintenanceDate?: string;
  nextMaintenanceDate?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface Proof {
  id: string;
  taskId: string;
  task?: Task;
  notes?: string;
  signature?: string;
  signedBy?: string;
  mediaItems?: ProofMediaItem[];
  createdAt: string;
  updatedAt: string;
}

export interface ProofMediaItem {
  id: string;
  proofId: string;
  url: string;
  key: string;
  type: "PHOTO" | "VIDEO" | "DOCUMENT";
  metadata?: Record<string, unknown>;
  createdAt: string;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

// Rates request/response
export interface RatesRequest {
  from: {
    address: Address;
    contact?: {
      name: string;
      phone?: string;
      email?: string;
    };
  };
  to: {
    address: Address;
    contact?: {
      name: string;
      phone?: string;
      email?: string;
    };
  };
  packages: {
    weight: number; // grams
    length: number; // cm
    width: number; // cm
    height: number; // cm
    value?: number; // cents
  }[];
  options?: {
    insurance?: boolean;
    signatureRequired?: boolean;
    saturdayDelivery?: boolean;
  };
}

export interface RatesResponse {
  quotes: {
    provider: Provider;
    carrierCode: string;
    carrierName: string;
    serviceCode: string;
    serviceName: string;
    listRate: number;
    retailRate: number;
    currency: string;
    estimatedDays?: number;
    estimatedDelivery?: string;
    quoteId: string; // Use this to book
    metadata?: Record<string, unknown>;
  }[];
  metadata?: {
    requestId: string;
    timestamp: string;
  };
}

// Booking request/response
export interface BookingRequest {
  quoteId: string;
  customerId?: string;
  metadata?: Record<string, unknown>;
}

export interface BookingResponse {
  order: Order;
  shipment: Shipment;
  tasks: Task[];
  trackingNumber?: string;
  labelUrl?: string;
}

// List response wrapper
export interface ListResponse<T> {
  data: T[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// ============================================================================
// Additional Models (for backward compatibility)
// ============================================================================

export interface ServiceArea {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  boundary: unknown; // GeoJSON polygon/multipolygon
  postcodes: string[];
  maxDailyOrders?: number | null;
  maxVehicles?: number | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: string;
  customerId: string;
  customer?: Customer;
  status?: string;
  // Typed columns (authoritative for display + queries)
  stripeInvoiceId?: string | null;
  totalCents?: number | null;
  dueDate?: string | null;
  dueDateOnSend?: string | null;
  // Raw Stripe snapshot — display-only fields (number, hosted_invoice_url, invoice_pdf)
  stripeInvoiceData?: {
    number?: string;
    hosted_invoice_url?: string | null;
    invoice_pdf?: string | null;
  } | null;
  totalOrders?: number;
  orderDateRange?: {
    start: string;
    end: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface GeoJSONPolygon {
  type: "Polygon";
  coordinates: number[][][];
}

export interface GeoJSONMultiPolygon {
  type: "MultiPolygon";
  coordinates: number[][][][];
}

export interface GeographicBoundary {
  id: string;
  name: string;
  code?: string;
  source: string;
  boundary: GeoJSONPolygon | GeoJSONMultiPolygon;
  metadata?: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AddressMedia {
  id: string;
  placeId?: string;
  streetNumber?: string;
  streetName: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  addressId?: string;
  address?: Address;
  mediaItems?: AddressMediaItem[];
  createdAt: string;
  updatedAt: string;
}

export interface AddressMediaItem {
  id: string;
  addressMediaId: string;
  key: string;
  url: string;
  type: string;
  mimeType?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Waypoint {
  id: string;
  tripId: string;
  sequence: number;
  locationKey: string; // "lat,lng" format from server
  location?: { lat: number; lng: number }; // Optional for compatibility
  radius?: number;
  tasks?: Task[];
  trip?: { color: string };
  // Server returns the ETA record object (not a string) once a trip is active.
  eta?: TripWaypointETA;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================================================
// Pricing-redesign — new entity types
// ============================================================================

/**
 * Normalized service identity per carrier. Replaces the legacy
 * `Carrier.metadata.discoveredServices` blob. Rate bands, dim factors, and
 * acceptance criteria all key on this row.
 */
export interface CarrierService {
  id: string;
  carrierId: string;
  carrier?: Carrier;
  code: string; // e.g. "ground", "express", "ez_fleet_same_day"
  name: string;
  estimatedDays?: number;
  isActive: boolean;
  metadata?: Record<string, unknown>;
  // Pricing-merger Phase 1: dim factor relocated from DimensionalWeightRule
  // to CarrierService directly. NULL = no DIM applied for this service's
  // label leg (carrier API handles its own DIM).
  dimFactorCm?: string | number | null;
  dimFactorInch?: string | number | null;
  // Phase C (#265): package fit ceilings. NULL = no limit enforced for that
  // dimension. Each ceiling short-circuits the quote to
  // PACKAGE_EXCEEDS_LIMITS when violated.
  maxWeightKg?: string | number | null;
  maxLongestSideCm?: string | number | null;
  maxLengthPlusGirthCm?: string | number | null;
  // Phase 14: CarrierProvider.id[] that surfaced this service in the most
  // recent discovery run. Empty = manually entered or never discovered.
  sourceProviderIds?: string[];
  firstSeenAt: string;
  lastSeenAt: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Self-rated label pricing for OWNED carriers (e.g. EZ Fleet). Lane × weight ×
 * validity window. External carriers' rates come from their API at quote time.
 */
export interface CarrierRateBand {
  id: string;
  carrierServiceId: string;
  carrierService?: CarrierService;
  fromServiceAreaId: string;
  toServiceAreaId: string;
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  currencyCode: string; // "CAD" today
  validFrom: string;
  validTo?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * TooEZ pickup tier. STANDARD today; product can add EXPRESS / SAME_DAY etc.
 * Dim factors are TooEZ's own (vans are space-constrained, tighter than carrier
 * rate cards).
 */
export interface PickupService {
  id: string;
  code: string; // lowercase snake_case, e.g. "standard"
  name: string;
  dimFactorCm?: number; // L×W×H (cm) ÷ this = dim weight (kg)
  dimFactorInch?: number; // L×W×H (in) ÷ this = dim weight (lb)
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Carrier-add-on-services platform: typed generalization of the old
 * PickupService. A CarrierAddOnService is a service a provider carrier offers
 * (pickup, packaging, insurance, signature). Type-specific data lives in 1:0..1
 * extensions — today only `pickupDetails` exists.
 */
export type CarrierAddOnServiceType =
  | "PICKUP"
  | "PACKAGING"
  | "INSURANCE"
  | "SIGNATURE"
  | "SPECIAL_HANDLING";

/**
 * PICKUP-only extension of CarrierAddOnService. dim factors are Prisma
 * `Decimal?` — serialized as strings (or null) over JSON.
 */
export interface PickupServiceDetails {
  carrierAddOnServiceId: string;
  dimFactorCm: string | null;
  dimFactorInch: string | null;
}

/**
 * INSURANCE-only extension of CarrierAddOnService. Fee fields are integer cents.
 * Either flat-fee OR percentage-with-clamps (server doesn't enforce mutual
 * exclusion — the form allows either pattern).
 */
export interface InsuranceServiceDetails {
  carrierAddOnServiceId: string;
  coverageMaxCents: number | null;
  currencyCode: string;
  flatFeeCents: number | null;
  percentageRate: number | null;
  minimumCents: number | null;
  maximumCents: number | null;
}

/**
 * PACKAGING-only extension of CarrierAddOnService. Two pricing modes:
 *  - `flatFeeCents`: integer cents, charged once per package.
 *  - `dimensionalPricing`: per-cubic-cm rate (cents-per-cm³) with a minimum
 *    cents floor. Server stores this as a JSON object.
 * The two modes are NOT mutually exclusive — admin discretion which to set.
 */
export interface PackagingServiceDetails {
  flatFeeCents: number | null;
  dimensionalPricing: {
    perCubicCm: number;
    minimumCents: number;
  } | null;
}

/**
 * SIGNATURE-only extension of CarrierAddOnService. Single integer-cents flat
 * fee — no percentage, no clamps. Nullable when the admin hasn't priced this
 * tier yet.
 */
export interface SignatureServiceDetails {
  flatFeeCents: number | null;
}

/**
 * SPECIAL_HANDLING-only extension of CarrierAddOnService. Rush / hazmat /
 * fragile etc. Same pricing shape as INSURANCE (flat or percentage with
 * min/max clamps), plus a free-form `notes` field for operator-facing
 * context. Mutual exclusion of flat vs percentage is NOT enforced server-
 * side — admin discretion which pattern to use.
 */
export interface SpecialHandlingServiceDetails {
  flatFeeCents: number | null;
  percentageRate: number | null;
  minimumCents: number | null;
  maximumCents: number | null;
  notes: string | null;
}

/**
 * CarrierAddOnService header. The `*Details` siblings are non-null only for
 * the matching `type`: `pickupDetails` for `PICKUP`, `insuranceDetails` for
 * `INSURANCE`, `packagingDetails` for `PACKAGING`, `signatureDetails` for
 * `SIGNATURE`, `specialHandlingDetails` for `SPECIAL_HANDLING`.
 */
export interface CarrierAddOnService {
  id: string;
  providerCarrierId: string;
  type: CarrierAddOnServiceType;
  code: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  pickupDetails: PickupServiceDetails | null;
  insuranceDetails?: InsuranceServiceDetails | null;
  packagingDetails?: PackagingServiceDetails | null;
  signatureDetails?: SignatureServiceDetails | null;
  specialHandlingDetails?: SpecialHandlingServiceDetails | null;
}

/**
 * Platform allowlist row — existence = "provider may offer this service type to
 * this label carrier". List/create responses include the `labelCarrier`
 * relation.
 */
export interface CarrierAddOnServiceGrant {
  id: string;
  providerCarrierId: string;
  serviceType: CarrierAddOnServiceType;
  labelCarrierId: string;
  createdAt: string;
  labelCarrier?: { id: string; code: string; name: string };
  // Default drop-off moved to per-(dispatch center, label carrier) config —
  // see DispatchCenterDropoffConfig / api/dispatch-center-dropoffs.ts.
}

/**
 * Carrier enablement row — a label carrier opting a specific
 * CarrierAddOnService in. `enabled: true` requires a backing grant. List/upsert
 * responses include the `labelCarrier` relation.
 */
export interface CarrierAddOnServiceAccess {
  id: string;
  carrierAddOnServiceId: string;
  labelCarrierId: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  labelCarrier?: { id: string; code: string; name: string };
}

/**
 * Junction linking a PICKUP `CarrierAddOnService` + `ServiceArea` + `TimeSlot`.
 * A row = "this pickup service serves this time slot in this service area".
 * Compound PK on the three ids. List/create responses include the
 * `serviceArea` + `timeSlot` relations.
 */
export interface PickupServiceTimeSlot {
  carrierAddOnServiceId: string;
  serviceAreaId: string;
  timeSlotId: string;
  createdAt: string;
  serviceArea: { id: string; code: string; name: string };
  timeSlot: { id: string; name: string; startTime: string; endTime: string };
}

/**
 * First leg of pickup pricing: sender → DispatchCenter. Pure geographic +
 * weight pricing. Carrier-specific second-leg costs live in
 * `RedistributionRateBand`.
 */
export interface PickupRateBand {
  id: string;
  pickupServiceId: string;
  pickupService?: PickupService;
  fromServiceAreaId: string;
  dispatchCenterId: string;
  dispatchCenter?: DispatchCenter;
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  rushHourSurchargeCents?: number;
  currencyCode: string;
  validFrom: string;
  validTo?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Second leg of pickup pricing: DispatchCenter → carrier intake.
 */
export interface RedistributionRateBand {
  id: string;
  dispatchCenterId: string;
  dispatchCenter?: DispatchCenter;
  carrierId: string;
  carrier?: Carrier;
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  currencyCode: string;
  validFrom: string;
  validTo?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * TooEZ-platform sortation facility, operated by a local-fleet carrier. Where
 * pickups land before redistribution to the destination carrier's intake.
 */
/** One internal run (mixed-task trip): departs = load deadline, returns = trip
 *  back at dispatch (>24:00 notation allowed for overnight, e.g. "25:00"). */
export interface DispatchRun {
  /** Stable run key, used to identify runs within a day. */
  id: string;
  departs: string;
  returns: string;
}

export type RunScheduleWeekday =
  | "mon"
  | "tue"
  | "wed"
  | "thu"
  | "fri"
  | "sat"
  | "sun";

/** Delivery estimates: internal trip schedule on a DispatchCenter. */
export interface DispatchRunSchedule {
  weekly: Record<RunScheduleWeekday, DispatchRun[]>;
  /** Date-keyed ("YYYY-MM-DD") full replacement of that day's runs.
   *  [] = no runs (holiday). */
  overrides: Record<string, DispatchRun[]>;
}

export interface DispatchCenter {
  id: string;
  code: string;
  name: string;
  /** Carrier that provides pickup into this dispatch (renamed from
   *  `operatorCarrierId` server-side; the create request contract still
   *  accepts `operatorCarrierId` for back-compat). */
  pickupProviderCarrierId: string;
  pickupProviderCarrier?: Carrier;
  addressId: string;
  address?: Address;
  serviceAreaId: string;
  serviceArea?: ServiceArea;
  /** Sender postal codes (FSAs) this dispatch covers. Used for selection. */
  postalCodes: string[];
  operatingHours?: Record<string, unknown>;
  /** Delivery estimates: minutes from pickup-slot end until packages are back
   *  at this dispatch, ready for the next run. */
  processingMinutes: number;
  timeZone: string;
  /** Delivery estimates: internal run schedule. Null = estimates disabled for
   *  this dispatch (customer web falls back to "N business days"). */
  runSchedule?: DispatchRunSchedule | null;
  isActive: boolean;
  isDefault?: boolean;
  canHandlePickup?: boolean;
  canHandleDelivery?: boolean;
  canHandleReturn?: boolean;
  canHandleTransfer?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * One row per scheduled pickup visit. Many shipments can attach to one booking
 * when sender + scheduledDate + slot + service all match. Per-shipment price
 * is frozen on `Shipment.pickupPriceCents`.
 */
export interface PickupBooking {
  id: string;
  pickupAddressId: string;
  pickupAddress?: Address;
  pickupServiceId: string;
  pickupService?: PickupService;
  dispatchCenterId: string;
  dispatchCenter?: DispatchCenter;
  scheduledDate: string;
  slotId: string;
  slot?: { id: string; name: string; startTime: string; endTime: string };
  /** Null = active; ISO timestamp = cancelled (and when). */
  cancelledAt: string | null;
  notes?: string;
  shipments?: Shipment[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Locked-in CAD conversion rate for non-CAD external carrier API rates.
 * Quote-time look up the latest active row for (fromCurrency, "CAD"); the rate
 * + original/converted amounts are persisted on `Shipment.pricingSnapshotJson`.
 * Manual ops entry today.
 */
export interface FxRate {
  id: string;
  fromCurrency: string; // "USD"
  toCurrency: string; // "CAD"
  rate: number;
  effectiveAt: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Pricing-redesign — get-quote request/response
// ============================================================================

/**
 * `POST /api/v2/pricing/get-quote` request body. No customer/serviceType inputs
 * (the new flow doesn't need them at browse time).
 */
/**
 * Address shape accepted by `/pricing/get-quote`. Server requires `name` OR
 * `company` (refinement on the Zod schema in
 * `ezx-server/src/features/addresses/validation.ts`), so the quote caller must
 * fold in contact details from the surrounding shipment input.
 */
export type GetQuoteAddress = Address & {
  name?: string;
  company?: string;
  phone?: string;
  email?: string;
};

export interface GetQuoteRequest {
  shipFromAddressBookEntryId?: string;
  pickupBookingId?: string;
  shipFrom: GetQuoteAddress;
  shipTo: GetQuoteAddress;
  packages: {
    weight: number;
    weightUnit?: "g" | "kg" | "lb" | "oz";
    length: number;
    width: number;
    height: number;
    dimensionUnit?: "cm" | "m" | "in" | "ft";
    packageKind?: "PARCEL" | "THICK_ENVELOPE";
    description?: string;
    declaredValueCents?: number;
  }[];
  customerId?: string;
}

/**
 * One option returned by `get-quote`. Label and pickup prices are split so the
 * pickup add-on can be displayed as its own line.
 *
 * The prices here are for DISPLAY. What the order is billed at comes from the
 * `quoteId` — a row the server wrote (#533).
 */
export interface PickupTimeSlot {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  sortOrder: number;
}

export interface PickupTierOption {
  pickupServiceId: string;
  pickupServiceCode: string;
  pickupServiceName: string;
  dispatch: { id: string; timeZone: string; city: string | null };
  pickupPriceCents: number;
  /** Output tax on this pickup leg at the sender's origin province rate. */
  taxCents: number;
  timeSlots: PickupTimeSlot[];
}

export interface PricingOption {
  carrier: {
    id: string;
    code: string;
    name: string;
    logoUrl?: string | null;
  };
  carrierService: {
    id: string;
    code: string;
    name: string;
    estimatedDays?: number;
  };
  /** Service-area IDs resolved server-side. Echo back in order create for drift detection. */
  fromServiceAreaId: string;
  toServiceAreaId: string;
  /**
   * The persisted quote this option was priced by. THIS is what an order is placed
   * with — the price below is only what to render.
   */
  quoteId: string;
  labelPriceCents: number;
  /** Output tax on the label leg at the sender's origin province rate. */
  taxCents: number;
  /** Per-tier pickup prices. Empty array = pickup unavailable for this option. */
  pickupOptions: PickupTierOption[];
  currency: "CAD";
  expiresAt: string;
}

export interface GetQuoteResponse {
  options: PricingOption[];
  addressesInServiceArea: boolean;
  correlationId: string;
  rejectionReasons?: { carrierId: string; reason: string }[];
}

// ============================================================================
// Pricing-redesign — order create request shape
// ============================================================================

/**
 * The chosen option, in the `POST /orders` body.
 *
 * ⚠️ THE ORDER CARRIES A QUOTE ID, NOT A PRICE (#533).
 *
 * `labelPriceCents` and `purchaseHandle` used to live here. The server billed the
 * price straight out of this body — so a client could send `1` for a $20 parcel and
 * pay a penny — and `purchaseHandle`, the token that BUYS the label, made a round
 * trip through the browser.
 *
 * Both are gone. Send the id; the server bills the row it wrote. The id is
 * worthless to forge precisely because the price is not in it.
 */
export interface SelectedRate {
  quoteId: string;
  carrierId: string;
  carrierServiceCode: string;
  fromServiceAreaId: string;
  toServiceAreaId: string;
  currency: string;
}

export interface PickupAddOn {
  pickupServiceId: string;
  scheduledDate: string; // ISO date
  slotId: string;
  notes?: string;
  /**
   * ⚠️ `pickupPriceCents` is gone from here too, and it was the more dangerous of
   * the two: credit redemption sized a customer's spending CAP from it, so sending
   * an inflated pickup price let a customer raise their own ceiling. Which TIER the
   * customer picks is still theirs. What that tier COSTS is not.
   */
}

export interface CreateOrderShipmentInput {
  selectedRate: SelectedRate;
  pickup?: PickupAddOn;
  /** Re-submit flag: set true when re-submitting after a 409 drift response. */
  confirmRePricing?: boolean;
  // Sender / recipient / shipFrom / shipTo / packages remain as before
  senderName: string;
  senderEmail?: string;
  senderPhone?: string;
  shipFrom: Address;
  recipientName: string;
  recipientEmail?: string;
  recipientPhone?: string;
  shipTo: Address;
  packages: {
    weightGrams: number;
    lengthCm: number;
    widthCm: number;
    heightCm: number;
    description?: string;
    isFragile?: boolean;
    requiresCooling?: boolean;
    declaredValueCents?: number;
  }[];
  deliveryNotes?: string;
}

/**
 * ⚠️ `PricingDriftResponse` IS DELETED. The server can no longer produce it.
 *
 * Drift meant "we recomputed your price, it moved more than 5%, confirm the new
 * one." That whole conversation presupposed a price in the request. There isn't one
 * (#533) — the order carries a quote id and the server bills the row it wrote.
 *
 * The only 409 this path produces now is `QUOTE_NOT_USABLE`, and the answer to it
 * is not "confirm this new price", it is "get a fresh quote."
 */
